import { GestRuntimeTranslatedText } from "@shared/library";
import React, { useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { getLanguageCode, languages } from "../../../../language";
import './Sidebar.scss';
//COMPONENTS
import Slide from '@mui/material/Slide';
//MODELS
import { MenuArea_model } from "../../../../models/ui_data/MenuArea_model";
//ICONS
import AppsIcon from '@mui/icons-material/Apps';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { menuIcons } from "../../../../assets/icons";
import { ReactComponent as MenuCloseIcon } from "../../../../assets/images/icons/menuClose.svg";
import { ReactComponent as MenuOpenIcon } from "../../../../assets/images/icons/menuOpen.svg";
//CONTEXTS
import { MenuOption_model } from "@optisewer/library";
import { CntxConfig } from '../../../Optisewer';

interface SidebarProps {
    isOpened: boolean;
    setIsOpened: React.Dispatch<React.SetStateAction<boolean>>;
    menu: MenuArea_model[];
}

const Sidebar = ({ menu, ...props }: SidebarProps) => {
    //CONTEXTS USE
    const cntxConfig = React.useContext(CntxConfig)!;

    //#region STATIC DATA
    const lang = languages(cntxConfig.settings?.lang);
    const navigate = useNavigate();
    const location = useLocation();
    //#endregion

    //#region STATES
    const [menuWindow, setMenuWindow] = useState<any>({ isOpened: false, top: 0 });
    const [selectedArea, setSelectedArea] = React.useState<MenuArea_model>();
    const [subMenu, setSubMenu] = React.useState<MenuOption_model[]>([]);
    const [windowSubMenu, setWindowSubMenu] = React.useState<any>({ label: '', subs: [] });
    const refSidebar = React.useRef<HTMLElement>(null);
    const refSidebarMenu = React.useRef<HTMLDivElement>(null);
    const topSideRef = React.useRef<HTMLDivElement>(null);
    const topMenuRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    //FLAGS
    const [showScrollArrow, setShowScrollArrow] = React.useState(false);
    //#endregion

    //#region METHODS
    const printMenuItem = (area: MenuArea_model, isHomeButton: boolean = false) => {
        const allDisabled = area.subs.every(s => !s.enabled);
        return <div key={area.id} className={`item ${selectedArea?.id === area.id ? 'selected' : ''} ${isHomeButton ? ' home' : ''} ${allDisabled ? 'disabled' : ''}`}
            onAuxClick={(e) => {
                if (area.subs.length === 1) {
                    const option = area.subs[0];
                    // Apri in una nuova scheda solo se è stato cliccato il tasto della rotellina
                    if (option.enabled && option.path && e.button === 1) window.open(option.path, '_blank');
                }
            }}
            onClick={() => {
                if (area.subs.length === 1) {
                    const option = area.subs[0];
                    if (option.enabled && option.path) {
                        setSelectedArea(area);
                        setSubMenu(area.subs);
                        props.setIsOpened(false);

                        navigate(option.path);
                    }
                }
                if (props.isOpened) {   // setta il submenu solo se aperto
                    setSubMenu(area.subs);
                    setSelectedArea(area);
                }
            }}
            onMouseEnter={event => {
                if (!props.isOpened) {  // popola il submenu della window
                    setWindowSubMenu(area);
                    openMenuWindow(event);
                }
            }}
            onMouseLeave={closeMenuWindow}
        >
            {renderMenuIcon(area.icon as keyof typeof menuIcons)}
        </div>;
    };

    const showArrow = () => {
        if (menuRef.current) {
            const showingArrow = menuRef.current.clientHeight < menuRef.current.scrollHeight &&
                (menuRef.current.scrollTop + menuRef.current.clientHeight) < menuRef.current.scrollHeight;
            setShowScrollArrow(showingArrow);
        }
    };

    const openMenuWindow = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const { y } = event.currentTarget.getBoundingClientRect();
        setMenuWindow({ isOpened: true, top: y });
    };
    const closeMenuWindow = () => setMenuWindow({ isOpened: false, top: 0 });

    const handleClickOutside = (event: any) => {
        if (!refSidebar.current?.contains(event.target) && !refSidebarMenu.current?.contains(event.target)) {
            // Clic al di fuori del div, eseguire l'azione desiderata qui
            props.setIsOpened(false);
        }
    };

    const renderMenuIcon = (icon: keyof typeof menuIcons) => {
        const Icon = menuIcons[icon] || AppsIcon;
        return <Icon />;
    };
    //#endregion

    //#region STATES CHANGE
    React.useEffect(() => {
        const observer = new ResizeObserver(entries => {
            if (entries.find(e => e.target === menuRef.current)) {
                showArrow();
            }
        });

        if (menuRef.current) {   // Collega l'osservatore alla tabella
            observer.observe(menuRef.current);
            menuRef.current.addEventListener('scroll', showArrow);
        }

        return () => {
            if (menuRef.current) {
                observer.unobserve(menuRef.current);
                menuRef.current.removeEventListener('scroll', showArrow);
            }
        };
    }, []);

    React.useEffect(() => {
        if (props.isOpened) {
            // Aggiunge un event listener al documento per gestire i clic
            document.addEventListener('click', handleClickOutside);

            // Pulisce l'event listener quando il componente si smonta
            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [props.isOpened]);

    React.useEffect(() => {
        const selected = menu.find(a => a.subs.find(s => location.pathname === s.path));

        setSelectedArea(selected);
        setSubMenu(selected?.subs ?? []);
    }, [menu, props.isOpened]);
    //#endregion

    return <>
        <aside id="gestSidebar" ref={refSidebar}>
            {menuWindow.isOpened &&
                <div
                    id="menuWindow"
                    style={{ top: menuWindow.top }}
                    onMouseEnter={() => setMenuWindow({ isOpened: true, top: menuWindow.top })}
                    onMouseLeave={closeMenuWindow}
                >
                    <div className='window'>
                        {windowSubMenu.hasArea &&
                            <div className="pageTitle"><h4><GestRuntimeTranslatedText text={windowSubMenu.label} targetLang={getLanguageCode(lang)} sourceLang="it" /></h4></div>
                        }

                        {windowSubMenu.subs.map((option: any, k: number) =>
                            <a key={k}
                                href={option.path}
                                className={`item ${option.enabled ? '' : 'disabled'} ${location.pathname === option.path ? 'selected' : ''}`}
                                onAuxClick={event => {
                                    // Apri in una nuova scheda solo se è stato cliccato il tasto della rotellina
                                    if (option.enabled && option.path && event.button === 1) {
                                        window.open(option.path, '_blank');
                                    };
                                }}
                                onClick={event => {
                                    event.preventDefault();

                                    if (option.enabled && option.path) {
                                        const area = menu[(menu.findIndex(v => v.id === option.areaID))];
                                        setSelectedArea(area);
                                        setSubMenu(area.subs);

                                        navigate(option.path);
                                    }
                                }}
                            >
                                {renderMenuIcon(option.icon as keyof typeof menuIcons)}
                                <h5><GestRuntimeTranslatedText text={option.label} targetLang={getLanguageCode(lang)} sourceLang="it" /></h5>
                            </a>
                        )}
                    </div>
                </div>
            }

            <div ref={topSideRef} className='topSide'>
                <div ref={topMenuRef} className="topMenu">
                    <div
                        onClick={() => props.setIsOpened(!props.isOpened)}
                        className="item"
                    >

                        {props.isOpened ? <MenuCloseIcon /> : <MenuOpenIcon />}
                    </div>

                    {/* {menu[0] && printMenuItem(menu[0], true)} */}
                </div>

                <div ref={menuRef} className="menu">
                    {menu.map((area) => printMenuItem(area))}
                </div>

                {showScrollArrow === true && <div className='scrollBelow'><ArrowDropDownRoundedIcon /></div>}
            </div>
        </aside>

        <Slide direction="right" in={props.isOpened}>
            <div
                ref={refSidebarMenu}
                id="menu"
                className={"menu " + (props.isOpened ? 'opened' : '')}
            >
                {selectedArea?.hasArea &&
                    <div className="pageTitle"><h4><GestRuntimeTranslatedText text={selectedArea?.label} targetLang={getLanguageCode(lang)} sourceLang="it" /></h4></div>
                }

                <div className='menuItems'>
                    <div className="items">
                        {subMenu.map((option, k) =>
                            <a key={k}
                                href={option.path}
                                className={`item ${option.enabled ? '' : 'disabled'} ${location.pathname === option.path ? 'selected' : ''}`}
                                onAuxClick={event => {
                                    // Apri in una nuova scheda solo se è stato cliccato il tasto della rotellina
                                    if (option.enabled && option.path && event.button === 1) {
                                        window.open(`${option.path}`, '_blank');
                                    };
                                }}
                                onClick={event => {
                                    event.preventDefault();

                                    if (option.enabled && option.path) {
                                        props.setIsOpened(false);
                                        navigate(option.path);
                                    }
                                }}
                            >
                                {renderMenuIcon(option.icon as keyof typeof menuIcons)}
                                <h5><GestRuntimeTranslatedText text={option.label} targetLang={getLanguageCode(lang)} sourceLang="it" /></h5>
                            </a>
                        )}
                    </div>
                </div>

                <div className='profile'>
                    <div>
                        <h4>{`${cntxConfig.session?.userData?.firstname} ${cntxConfig.session?.userData?.lastname}`}</h4>
                        <h5>@{cntxConfig.session?.userData?.username}</h5>
                    </div>
                </div>
            </div>
        </Slide></>;
};
export default Sidebar;